import { UserRole } from "@fuse/recoil/pluginStudio/types"

import { ExamplePlugin } from "../../examples/types"

export const zeroConfigLinksPlugin: ExamplePlugin = {
  id: "links",
  name: "Links",
  description: "Share links to event guides, menus, playlists, and more.",
  category: "Zero-Config",
  tags: ["zero-config", "links", "resources", "instant"],
  difficulty: "beginner",
  preview:
    "Instant link sharing with glasomorphism design - no configuration needed",
  iconName: "links",
  showHostViewInDetails: false, // Show guest view in event details
  requiresJoinToView: false, // Links are accessible to all users without joining
  files: {
    [UserRole.GUEST]: `import React, { useState, useEffect } from 'react';

export default function GuestView({ pluginApi, pluginComponents, eventMetadata, userRole, isLoggedIn }) {
  const {
    PluginContainer,
    LoadingSpinner
  } = pluginComponents || {};

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pluginLabel, setPluginLabel] = useState("Quick Links");

  // Load links on mount
  useEffect(() => {
    async function loadLinks() {
      try {
        const data = await pluginApi.getPluginEventInstanceData();
        // Filter out invalid URLs in guest view
        const validLinks = (data?.links || []).filter(link => {
          if (!link.url.trim()) return false;
          const validation = pluginApi.validateUrl(link.url);
          return validation.isValid && validation.isAllowed;
        });
        setLinks(validLinks);
        setPluginLabel(data?.label || "Quick Links");
      } catch (error) {
        console.error('Error loading links:', error);
      } finally {
        setLoading(false);
      }
    }
    loadLinks();
  }, []);

  const handleLinkClick = (url) => {
    // Use the new navigation API with validation
    const result = pluginApi.openUrl(url, '_blank');
    
    if (!result.isAllowed) {
      // Show user feedback for blocked URLs (shouldn't happen in guest view now)
      alert(\`This link is blocked for security reasons: \${result.reason}\`);
    }
  };

  if (loading) {
    return (
      <PluginContainer>
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      </PluginContainer>
    );
  }

  return (
    <PluginContainer>
      <div className="space-y-1 max-w-screen-sm w-full">
        
        {links.map((link) => (
          <div
            key={link.id}
            className="flex gap-3 py-2 px-2 cursor-pointer group hover:bg-white/5 rounded-lg transition-all duration-200"
            onClick={() => handleLinkClick(link.url)}
          >
            <div className="text-xl flex-shrink-0 leading-6">
              {link.emoji || '🔗'}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-base font-medium text-white group-hover:text-blue-300 transition-colors leading-6 break-all max-w-full">
                {link.title || link.url}
              </div>
              {link.description && (
                <div className="text-white/70 text-sm mt-1 leading-relaxed">
                  {link.description}
                </div>
              )}
            </div>
          </div>
        ))}

        {links.length === 0 && (
          <div className="mt-1 flex items-center justify-center">
            <p className="text-white/70 text-sm">No links shared yet</p>
          </div>
        )}
      </div>
    </PluginContainer>
  );
}`,
    [UserRole.HOST_EVENT_VIEW]: `import React, { useState, useEffect, useCallback } from 'react';

export default function HostView({ pluginApi, pluginComponents, eventMetadata, userRole, isLoggedIn }) {
  const {
    PluginContainer,
    TextInput,
    SecondaryButton,
    EmojiPicker,
    LoadingSpinner
  } = pluginComponents || {};

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [focusedLinkId, setFocusedLinkId] = useState(null);
  const [pluginLabel, setPluginLabel] = useState("Quick Links");

  // Debounced save function
  const debouncedSave = useCallback(
    (() => {
      let timeoutId
      return (linksToSave, label) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(async () => {
          setSaving(true)
          try {
            // Filter out empty links before saving
            const validLinks = linksToSave.filter((link) => link.url.trim())
            await pluginApi.savePluginEventInstanceData({
              links: validLinks,
              label: label || "Quick Links"
            })
          } catch (error) {
            console.error("Error saving links:", error)
          } finally {
            setSaving(false)
          }
        }, 1000) // Save 1 second after user stops typing
      }
    })(),
    [pluginApi],
  )

  // Load links on mount
  useEffect(() => {
    async function loadLinks() {
      try {
        const data = await pluginApi.getPluginEventInstanceData()
        const loadedLinks = data?.links || []
        const savedLabel = data?.label || "Quick Links"
        
        setPluginLabel(savedLabel)
        
        // Always ensure there's at least one empty link initially
        if (loadedLinks.length === 0) {
          setLinks([createEmptyLink()])
        } else {
          setLinks([...loadedLinks])
        }
      } catch (error) {
        console.error("Error loading links:", error)
        setLinks([createEmptyLink()])
      } finally {
        setLoading(false)
      }
    }
    loadLinks()
  }, [])

  const createEmptyLink = () => ({
    id: Date.now() + Math.random(),
    url: "",
    title: "",
    description: "",
    emoji: "🔗",
  })

  const updateLink = (id, field, value) => {
    setLinks((prevLinks) => {
      const newLinks = prevLinks.map((link) =>
        link.id === id ? { ...link, [field]: value } : link,
      )

      // Remove the automatic creation of new empty link when typing
      // Auto-save when links change
      debouncedSave(newLinks, pluginLabel)

      return newLinks
    })
  }

  const updateLabel = (newLabel) => {
    setPluginLabel(newLabel)
    debouncedSave(links, newLabel)
  }

  const deleteLink = async (id) => {
    const newLinks = links.filter((link) => link.id !== id)
    // Don't automatically add empty link when deleting
    setLinks(newLinks)

    // Immediately save when deleting
    setSaving(true)
    try {
      const validLinks = newLinks.filter((link) => link.url.trim())
      await pluginApi.savePluginEventInstanceData({
        links: validLinks,
        label: pluginLabel
      })
    } catch (error) {
      console.error("Error saving links:", error)
    } finally {
      setSaving(false)
    }
  }

  const handleAddLink = () => {
    setLinks((prevLinks) => [...prevLinks, createEmptyLink()])
  }

  const getErrorMessage = (validation) => {
    if (!validation) return ""
    if (!validation.isValid) return "Invalid URL format"
    if (!validation.isAllowed) return "URL not allowed"
    return ""
  }

  if (loading) {
    return (
      <PluginContainer>
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      </PluginContainer>
    );
  }

  return (
    <PluginContainer>
      <div className="space-y-3">

        {links.map((link, index) => {
          const isLastLink = index === links.length - 1
          const isEmpty =
            !link.url.trim() && !link.title.trim() && !link.description.trim()
          const isEmptyLastLink = isLastLink && isEmpty
          const isFocused = focusedLinkId === link.id
          const hasContent =
            link.url.trim() || link.title.trim() || link.description.trim()

          // Validate URL if it exists
          const validation = link.url.trim()
            ? pluginApi?.validateUrl(link?.url)
            : null
          const errorMessage = getErrorMessage(validation)

          return (
            <div key={link.id} className="space-y-3">
              {/* URL Row - Clean, no boxes, better alignment */}
              <div className="flex flex-row items-center gap-3">
                <EmojiPicker
                  value={link.emoji}
                  onChange={(value) => updateLink(link.id, "emoji", value)}
                />
                <div className="flex-1">
                  <TextInput
                    value={link.url}
                    onChange={(value) => updateLink(link.id, "url", value)}
                    placeholder={"URL"}
                    type="url"
                    error={errorMessage}
                    onFocus={() => setFocusedLinkId(link.id)}
                    onBlur={() => {
                      // Only clear focus if the link is empty
                      if (!hasContent) {
                        setTimeout(() => setFocusedLinkId(null), 100)
                      }
                    }}
                  />
                </div>
                {(isFocused || hasContent) && !isEmptyLastLink && (
                  <SecondaryButton
                    onClick={() => deleteLink(link.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/20 bg-transparent"
                    title="Delete link"
                    icon={
                      <svg
                        width="10"
                        height="12"
                        viewBox="0 0 10 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2.75391 11.8486C2.37305 11.8486 2.06868 11.7412 1.84082 11.5264C1.61621 11.3148 1.49414 11.0186 1.47461 10.6377L1.14746 3.60645H0.610352C0.489909 3.60645 0.385742 3.56413 0.297852 3.47949C0.213216 3.39486 0.170898 3.29069 0.170898 3.16699C0.170898 3.04655 0.213216 2.94401 0.297852 2.85938C0.385742 2.77474 0.489909 2.73242 0.610352 2.73242H9.38477C9.50846 2.73242 9.61263 2.77474 9.69727 2.85938C9.7819 2.94076 9.82422 3.04329 9.82422 3.16699C9.82422 3.29069 9.7819 3.39486 9.69727 3.47949C9.61263 3.56413 9.50846 3.60645 9.38477 3.60645H8.84766L8.51562 10.6377C8.49935 11.0153 8.37728 11.3115 8.14941 11.5264C7.92155 11.7412 7.61882 11.8486 7.24121 11.8486H2.75391ZM2.85156 10.9697H7.13379C7.27051 10.9697 7.38281 10.9242 7.4707 10.833C7.56185 10.7451 7.61068 10.6279 7.61719 10.4814L7.92969 3.60645H2.0459L2.37793 10.4814C2.38444 10.6247 2.43164 10.7419 2.51953 10.833C2.60742 10.9242 2.7181 10.9697 2.85156 10.9697ZM3.55957 10.1836C3.46191 10.1836 3.38053 10.1543 3.31543 10.0957C3.25358 10.0371 3.22266 9.95898 3.22266 9.86133L3.07129 4.75391C3.07129 4.65951 3.10059 4.58301 3.15918 4.52441C3.22103 4.46257 3.30241 4.43164 3.40332 4.43164C3.50423 4.43164 3.58561 4.46094 3.64746 4.51953C3.71257 4.57812 3.74674 4.65462 3.75 4.74902L3.89648 9.85645C3.89974 9.9541 3.87044 10.0339 3.80859 10.0957C3.74674 10.1543 3.66374 10.1836 3.55957 10.1836ZM5 10.1836C4.89583 10.1836 4.8112 10.1543 4.74609 10.0957C4.68424 10.0339 4.65332 9.95573 4.65332 9.86133V4.75391C4.65332 4.65625 4.68424 4.57812 4.74609 4.51953C4.8112 4.46094 4.89583 4.43164 5 4.43164C5.10091 4.43164 5.18229 4.46094 5.24414 4.51953C5.30924 4.57812 5.3418 4.65625 5.3418 4.75391V9.86133C5.3418 9.95573 5.30924 10.0339 5.24414 10.0957C5.18229 10.1543 5.10091 10.1836 5 10.1836ZM6.43555 10.1836C6.33138 10.1836 6.24837 10.1543 6.18652 10.0957C6.12467 10.0339 6.09538 9.9541 6.09863 9.85645L6.24512 4.75391C6.24837 4.65625 6.28092 4.57812 6.34277 4.51953C6.40788 4.46094 6.48926 4.43164 6.58691 4.43164C6.69108 4.43164 6.77246 4.46257 6.83105 4.52441C6.8929 4.58301 6.92383 4.65951 6.92383 4.75391L6.77246 9.86133C6.77246 9.95898 6.73991 10.0371 6.6748 10.0957C6.61296 10.1543 6.5332 10.1836 6.43555 10.1836ZM2.77344 3.05469V2.00488C2.77344 1.63053 2.88574 1.33757 3.11035 1.12598C3.33822 0.914388 3.65723 0.808594 4.06738 0.808594H5.92285C6.32975 0.808594 6.64714 0.914388 6.875 1.12598C7.10286 1.33757 7.2168 1.63053 7.2168 2.00488V3.05469H6.29883V2.04883C6.29883 1.92513 6.25814 1.82585 6.17676 1.75098C6.09863 1.67611 5.99284 1.63867 5.85938 1.63867H4.12598C3.99251 1.63867 3.88509 1.67611 3.80371 1.75098C3.72559 1.82585 3.68652 1.92513 3.68652 2.04883V3.05469H2.77344Z"
                          fill="white"
                        />
                      </svg>
                    }
                  />
                )}
              </div>

              {(isFocused || hasContent) && !isEmptyLastLink && (
                <div className="space-y-3 ml-13 flex flex-col">
                  <TextInput
                    value={link.title}
                    onChange={(value) => updateLink(link.id, "title", value)}
                    placeholder="Name this link (optional)"
                  />
                  <TextInput
                    value={link.description}
                    onChange={(value) =>
                      updateLink(link.id, "description", value)
                    }
                    placeholder="Description (optional)"
                  />
                </div>
              )}
              {!isLastLink && <div className="border-b border-white/10 mt-3" />}
            </div>
          )
        })}

        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={handleAddLink}
            className="flex items-center gap-2 text-white/80 hover:text-white mt-2 text-sm bg-transparent"
          >
            <span className="text-xl">＋</span> Add another link
          </button>
        </div>

        {/* Auto-save indicator */}
        {saving && (
          <div className="text-center text-white/60 text-sm flex items-center justify-center gap-2 py-2">
            <div className="animate-spin rounded-full border-2 border-white/20 border-t-white w-3 h-3" />
            Saving...
          </div>
        )}
        
        {/* Security info */}
        <div className="text-center text-white/40 text-xs py-2">
          Only trusted domains are allowed for security. Supported: Social media, payments, maps, and more.
        </div>
      </div>
    </PluginContainer>
  );
}`,
    [UserRole.HOST_CONFIG_VIEW]: `import React from 'react';

export default function HostConfigView({ pluginComponents }) {
  const { PluginContainer } = pluginComponents || {};

  return (
    <PluginContainer>
      <div className="text-center py-8">
        <div className="text-4xl mb-4">🎉</div>
        <h3 className="text-white/80 text-lg font-medium mb-2">Zero Configuration Required!</h3>
        <p className="text-white/60 text-sm mb-4">
          Links are automatically shared with guests. No setup needed!
        </p>
        <div className="text-white/40 text-xs">
          <p className="mb-2">🔒 Security: Only trusted domains are allowed</p>
          <p className="mb-2">🏷️ Custom Label: You can customize the plugin label in the event view</p>
          <p>Supported: Social media, payments, maps, communication tools, and more</p>
        </div>
      </div>
    </PluginContainer>
  );
}`,
  },
}
